import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { roleLabel, fmtDateTime, fmtDate, initials } from "@/lib/cc";
import Modal from "@/components/Modal";
import {
  Users2, UserPlus, Shield, KeyRound, Power, RefreshCw, Search, FileDown,
  Download, AlertTriangle, CheckCircle2, X, ChevronRight, Filter, Lock,
  ArrowLeft, Mail, Phone, Briefcase, Building2, BadgeCheck, Settings2, History,
} from "lucide-react";

const ROLES = [
  { key: "super_admin", label: "Super Admin" },
  { key: "hr_admin", label: "HR Admin" },
  { key: "recruiter", label: "Recruiter" },
  { key: "interviewer", label: "Interviewer" },
  { key: "nominated_individual", label: "Nominated Individual" },
  { key: "finance", label: "Finance" },
];

const ROLE_PILL = {
  super_admin: "pill-danger",
  hr_admin: "pill-info",
  recruiter: "pill-sage",
  interviewer: "pill-warning",
  nominated_individual: "pill-info",
  finance: "pill-neutral",
};

// =============== USERS LIST ===============
export function AdminUsersPage() {
  const { user: me } = useAuth();
  const [data, setData] = useState({ users: [], dash: null });
  const [filters, setFilters] = useState({ role: "", status_filter: "", department: "", q: "" });
  const [showNew, setShowNew] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
    const [u, d] = await Promise.all([
      api.get("/admin/users", { params }),
      api.get("/admin/users/dashboard"),
    ]);
    setData({ users: u.data, dash: d.data });
  };
  useEffect(() => { load(); }, [filters]);

  if (me?.role !== "super_admin" && me?.role !== "hr_admin") {
    return <div className="cc-card p-8 text-[var(--cc-text-soft)]">Insufficient permissions.</div>;
  }

  const dash = data.dash;

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">Provisioning</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Users2 className="w-8 h-8" />User Management</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Manage management-staff accounts, roles, permissions and MFA.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)} data-testid="new-user-button">
          <UserPlus className="w-4 h-4" /> New user
        </button>
      </div>

      {/* Widgets */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Widget label="Total" value={dash.total} />
          <Widget label="Active" value={dash.by_status.active || 0} tone="var(--cc-success)" />
          <Widget label="Inactive" value={dash.by_status.inactive || 0} tone="var(--cc-text-soft)" />
          <Widget label="MFA pending" value={dash.mfa_pending} tone={dash.mfa_pending > 0 ? "var(--cc-accent)" : undefined} />
          <Widget label="Nominated Individuals" value={dash.ni_count} tone={dash.ni_count < 2 ? "var(--cc-accent)" : "var(--cc-success)"} sub={dash.ni_count < 2 ? "Backup required" : "Backup ✓"} />
        </div>
      )}

      {/* Filters */}
      <div className="cc-card p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--cc-text-soft)]" />
          <input className="cc-input pl-9" placeholder="Search by name or email…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="users-search-input" />
        </div>
        <select className="cc-select max-w-[180px]" value={filters.role} onChange={(e) => setFilters({ ...filters, role: e.target.value })} data-testid="users-role-filter">
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select className="cc-select max-w-[140px]" value={filters.status_filter} onChange={(e) => setFilters({ ...filters, status_filter: e.target.value })} data-testid="users-status-filter">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">User</th>
              <th className="px-5 py-3 label-eyebrow">Role</th>
              <th className="px-5 py-3 label-eyebrow">Department</th>
              <th className="px-5 py-3 label-eyebrow">Status</th>
              <th className="px-5 py-3 label-eyebrow">MFA</th>
              <th className="px-5 py-3 label-eyebrow">Last login</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} className="border-t border-[var(--cc-border)] hover:bg-[var(--cc-surface)] cursor-pointer" onClick={() => nav(`/app/admin/users/${u.id}`)} data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: "var(--cc-primary)" }}>
                      {initials(u.first_name || u.name?.split(" ")[0], u.last_name || u.name?.split(" ")[1])}
                    </div>
                    <div>
                      <div className="font-semibold">{u.name}</div>
                      <div className="text-xs text-[var(--cc-text-soft)]">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3"><span className={`pill ${ROLE_PILL[u.role] || "pill-neutral"}`}>{roleLabel(u.role)}</span></td>
                <td className="px-5 py-3 text-[var(--cc-text-soft)]">{u.department || "—"}</td>
                <td className="px-5 py-3">
                  <span className={`pill ${u.status === "active" ? "pill-success" : "pill-neutral"}`}>{u.status || "active"}</span>
                </td>
                <td className="px-5 py-3">
                  {u.mfa_enabled ? <span className="pill pill-success">Enabled</span> : <span className="pill pill-warning">Pending</span>}
                </td>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)]">{u.last_login ? fmtDateTime(u.last_login) : "Never"}</td>
                <td className="px-5 py-3 text-right text-[var(--cc-text-soft)]"><ChevronRight className="w-4 h-4 inline" /></td>
              </tr>
            ))}
            {data.users.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-[var(--cc-text-soft)]">No users found.</td></tr>}
          </tbody>
        </table>
      </div>

      {showNew && <NewUserModal me={me} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function Widget({ label, value, sub, tone }) {
  return (
    <div className="cc-card p-4">
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="font-dash text-3xl font-bold" style={{ color: tone }}>{value}</div>
      {sub && <div className="text-xs mt-1" style={{ color: tone || "var(--cc-text-soft)" }}>{sub}</div>}
    </div>
  );
}

function NewUserModal({ me, onClose, onCreated }) {
  const allowedRoles = me.role === "super_admin"
    ? ROLES
    : ROLES.filter((r) => ["recruiter", "interviewer"].includes(r.key));
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", job_title: "",
    department: "", role: allowedRoles[0]?.key || "recruiter",
    salary_approval_limit: "", approval_authority: "both",
    start_date: "", mfa_required: true,
  });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.email) return toast.error("Name and email required");
    setBusy(true);
    try {
      const payload = { ...form, salary_approval_limit: form.salary_approval_limit ? Number(form.salary_approval_limit) : null };
      const { data } = await api.post("/admin/users", payload);
      setCreated(data);
      toast.success("User created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create user");
    } finally { setBusy(false); }
  };

  if (created) {
    return (
      <Modal onClose={onClose} widthClass="max-w-lg">
        <div className="flex items-center gap-2 mb-3 text-[var(--cc-success)]">
          <CheckCircle2 className="w-5 h-5" /><span className="font-semibold">User created</span>
        </div>
        <h2 className="font-display text-2xl font-bold mb-4">{created.name}</h2>
        <div className="p-4 rounded-lg bg-[var(--cc-surface)] mb-4">
          <div className="label-eyebrow mb-1">Temporary password (one-time)</div>
          <div className="font-dash text-xl font-bold mb-2" data-testid="new-user-temp-password">{created.temp_password}</div>
          <p className="text-xs text-[var(--cc-text-soft)]">Valid 24 hours. Forces password change on first login. A welcome email is sent automatically with the temporary password.</p>
        </div>
        <div className="flex justify-end gap-2">
          <button className="btn-primary" onClick={() => { onCreated(); }} data-testid="new-user-done">Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} testId="new-user-modal" widthClass="max-w-2xl">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-bold">Create user</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="label-eyebrow block mb-1">First name</label>
          <input className="cc-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="new-user-first-name" /></div>
        <div><label className="label-eyebrow block mb-1">Last name</label>
          <input className="cc-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="new-user-last-name" /></div>
        <div><label className="label-eyebrow block mb-1">Email</label>
          <input type="email" className="cc-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
        <div><label className="label-eyebrow block mb-1">Phone</label>
          <input className="cc-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
        <div><label className="label-eyebrow block mb-1">Role</label>
          <select className="cc-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
            {allowedRoles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          {me.role === "hr_admin" && <div className="text-[10px] mt-1 text-[var(--cc-text-soft)]">HR Admin can only create Recruiters & Interviewers</div>}
        </div>
        <div><label className="label-eyebrow block mb-1">Department</label>
          <input className="cc-input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
        <div><label className="label-eyebrow block mb-1">Job title</label>
          <input className="cc-input" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
        <div><label className="label-eyebrow block mb-1">Start date</label>
          <input type="date" className="cc-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
        {(form.role === "nominated_individual" || form.role === "finance") && (
          <>
            <div><label className="label-eyebrow block mb-1">Approval authority</label>
              <select className="cc-select" value={form.approval_authority} onChange={(e) => setForm({ ...form, approval_authority: e.target.value })}>
                <option value="conditional">Conditional offers</option>
                <option value="final">Final offers</option>
                <option value="both">Both</option>
              </select></div>
            <div><label className="label-eyebrow block mb-1">Salary approval limit (£)</label>
              <input type="number" className="cc-input" value={form.salary_approval_limit} onChange={(e) => setForm({ ...form, salary_approval_limit: e.target.value })} placeholder="60000" />
              {form.role === "finance" && form.salary_approval_limit > 60000 && (
                <div className="text-[10px] mt-1 text-[var(--cc-accent)]">Offers over £60k will require dual approval</div>
              )}
            </div>
          </>
        )}
      </div>
      <div className="mt-4 p-3 rounded-lg bg-[var(--cc-surface)] flex items-center gap-2 text-xs text-[var(--cc-text-soft)]">
        <Shield className="w-4 h-4 shrink-0" />
        <span>MFA required for all roles. Welcome email + temporary password (24h validity) is issued on creation.</span>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="new-user-submit">{busy ? "Creating…" : "Create user"}</button>
      </div>
    </Modal>
  );
}

// =============== USER DETAIL ===============
export function AdminUserDetailPage() {
  const { id } = useParams();
  const { user: me } = useAuth();
  const [u, setU] = useState(null);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("profile");
  const nav = useNavigate();

  const load = async () => {
    const [user, log] = await Promise.all([
      api.get(`/admin/users/${id}`),
      api.get("/admin/audit", { params: { target_user_id: id, limit: 50 } }),
    ]);
    setU(user.data); setAudit(log.data);
  };
  useEffect(() => { load(); }, [id]);

  if (!u) return <div className="text-[var(--cc-text-soft)]">Loading…</div>;
  const isSuper = me.role === "super_admin";

  const tabs = [
    { k: "profile", l: "Profile", icon: Users2 },
    { k: "role", l: "Role & Permissions", icon: Shield },
    { k: "assignments", l: "Assignments", icon: Briefcase },
    { k: "security", l: "Security", icon: Lock },
    { k: "onboarding", l: "Onboarding", icon: BadgeCheck },
    { k: "audit", l: "Audit", icon: History },
    { k: "lifecycle", l: "Lifecycle", icon: Power },
  ];

  return (
    <div className="fade-up">
      <button className="btn-ghost mb-3" onClick={() => nav("/app/admin/users")} data-testid="back-to-users"><ArrowLeft className="w-4 h-4 inline mr-1" />All users</button>
      <div className="cc-card p-6 mb-5">
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-xl" style={{ background: "var(--cc-primary)" }}>
            {initials(u.first_name, u.last_name)}
          </div>
          <div className="flex-1">
            <h1 className="font-display text-3xl font-bold">{u.name}</h1>
            <div className="text-[var(--cc-text-soft)] mt-1">{u.job_title || roleLabel(u.role)} · {u.department || "—"}</div>
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-[var(--cc-text-soft)]">
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</span>
              {u.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone}</span>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`pill ${ROLE_PILL[u.role]}`}>{roleLabel(u.role)}</span>
            <span className={`pill ${u.status === "active" ? "pill-success" : "pill-neutral"}`}>{u.status}</span>
            {u.mfa_enabled ? <span className="pill pill-success">MFA on</span> : <span className="pill pill-warning">MFA pending</span>}
          </div>
        </div>
      </div>

      <div className="border-b border-[var(--cc-border)] flex items-center gap-2 mb-5 overflow-x-auto cc-scroll">
        {tabs.map((t) => (
          <button key={t.k} className={`tab-btn flex items-center gap-1.5 ${tab === t.k ? "active" : ""}`} onClick={() => setTab(t.k)} data-testid={`user-tab-${t.k}`}>
            <t.icon className="w-3.5 h-3.5" />{t.l}
          </button>
        ))}
      </div>

      {tab === "profile" && <ProfileTab u={u} />}
      {tab === "role" && <RoleTab u={u} isSuper={isSuper} reload={load} />}
      {tab === "assignments" && <AssignmentsTab u={u} reload={load} />}
      {tab === "security" && <SecurityTab u={u} isSuper={isSuper} reload={load} />}
      {tab === "onboarding" && <OnboardingTab u={u} isSuper={isSuper} reload={load} />}
      {tab === "audit" && <AuditTab audit={audit} />}
      {tab === "lifecycle" && <LifecycleTab u={u} isSuper={isSuper} reload={load} />}
    </div>
  );
}

function ProfileTab({ u }) {
  const item = (label, value) => (
    <div><div className="label-eyebrow mb-1">{label}</div><div className="text-sm">{value || "—"}</div></div>
  );
  return (
    <div className="cc-card p-6 grid grid-cols-2 gap-y-4">
      {item("Email", u.email)}
      {item("Phone", u.phone)}
      {item("Job title", u.job_title)}
      {item("Department", u.department)}
      {item("Start date", fmtDate(u.start_date))}
      {item("Created", fmtDateTime(u.created_at))}
      {item("Last login", u.last_login ? fmtDateTime(u.last_login) : "Never")}
      {item("Reports to", u.reports_to)}
      {(u.salary_approval_limit !== null && u.salary_approval_limit !== undefined) && item("Salary approval limit", `£${u.salary_approval_limit?.toLocaleString?.() || u.salary_approval_limit}`)}
      {u.approval_authority && item("Approval authority", u.approval_authority)}
    </div>
  );
}

function RoleTab({ u, isSuper, reload }) {
  const [newRole, setNewRole] = useState(u.role);
  const [reason, setReason] = useState("");
  const [perms, setPerms] = useState(u.permissions || {});

  const changeRole = async () => {
    if (!isSuper) return toast.error("Only Super Admin");
    try {
      await api.post(`/admin/users/${u.id}/role`, { new_role: newRole, reason });
      toast.success("Role changed");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const savePerms = async () => {
    try {
      await api.post(`/admin/users/${u.id}/permissions`, { permissions: perms });
      toast.success("Permissions updated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="cc-card p-6">
        <h3 className="font-dash font-bold mb-3">Change role</h3>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="label-eyebrow block mb-1">Current</label><div className="text-sm font-semibold">{roleLabel(u.role)}</div></div>
          <div><label className="label-eyebrow block mb-1">New role</label>
            <select className="cc-select" value={newRole} onChange={(e) => setNewRole(e.target.value)} disabled={!isSuper} data-testid="change-role-select">
              {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select></div>
          <div><label className="label-eyebrow block mb-1">Reason (optional)</label>
            <input className="cc-input" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
        </div>
        <div className="flex justify-end mt-3">
          <button className="btn-primary" onClick={changeRole} disabled={!isSuper || newRole === u.role} data-testid="change-role-button">Save role change</button>
        </div>
      </div>

      <div className="cc-card p-6">
        <h3 className="font-dash font-bold mb-3">Permissions matrix</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Object.entries(perms).map(([k, v]) => (
            <label key={k} className="flex items-center gap-2 p-2 rounded hover:bg-[var(--cc-surface)] cursor-pointer">
              <input type="checkbox" checked={!!v} onChange={(e) => setPerms({ ...perms, [k]: e.target.checked })} disabled={!isSuper} data-testid={`perm-${k}`} />
              <span className="text-sm">{k.replace(/_/g, " ")}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <button className="btn-primary" onClick={savePerms} disabled={!isSuper} data-testid="save-perms-button">Save permissions</button>
        </div>
      </div>
    </div>
  );
}

function AssignmentsTab({ u, reload }) {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(u.assigned_job_ids || []);
  const [deadline, setDeadline] = useState(u.screening_deadline?.slice(0, 10) || "");

  useEffect(() => { api.get("/jobs").then(({ data }) => setJobs(data)); }, []);

  const save = async () => {
    if (u.role === "recruiter" && selected.length === 0) return toast.error("Recruiter needs at least one assigned job");
    try {
      await api.post(`/admin/users/${u.id}/assign-jobs`, {
        job_ids: selected,
        screening_deadline: deadline ? new Date(deadline).toISOString() : null,
      });
      toast.success("Assignments updated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="cc-card p-6">
      <h3 className="font-dash font-bold mb-3">Job assignments</h3>
      <p className="text-sm text-[var(--cc-text-soft)] mb-4">Recruiters can see only candidates for jobs assigned to them. Auto-alerts fire 3 days before, on, and after the screening deadline (<strong>MOCKED</strong>).</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        {jobs.map((j) => (
          <label key={j.id} className="flex items-start gap-2 p-3 border border-[var(--cc-border)] rounded-lg cursor-pointer hover:bg-[var(--cc-surface)]">
            <input type="checkbox" className="mt-1" checked={selected.includes(j.id)}
              onChange={(e) => setSelected(e.target.checked ? [...selected, j.id] : selected.filter((x) => x !== j.id))}
              data-testid={`assign-job-${j.id}`} />
            <div className="flex-1">
              <div className="text-sm font-semibold">{j.title}</div>
              <div className="text-xs text-[var(--cc-text-soft)]">{j.location} · {j.applicant_count} applicants</div>
            </div>
          </label>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div><label className="label-eyebrow block mb-1">Screening deadline</label>
          <input type="date" className="cc-input" value={deadline} onChange={(e) => setDeadline(e.target.value)} /></div>
      </div>
      <div className="flex justify-end mt-4">
        <button className="btn-primary" onClick={save} data-testid="save-assignments">Save assignments</button>
      </div>
    </div>
  );
}

function SecurityTab({ u, isSuper, reload }) {
  const [tempPw, setTempPw] = useState(null);

  const resetPw = async () => {
    if (!isSuper) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, { method: "temporary" });
      setTempPw(data.temp_password);
      toast.success("Password reset");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="cc-card p-6 space-y-5">
      <div>
        <h3 className="font-dash font-bold mb-2">Password</h3>
        <div className="flex items-center gap-3">
          <button className="btn-secondary" onClick={resetPw} disabled={!isSuper} data-testid="reset-password-button"><KeyRound className="w-4 h-4" /> Reset password</button>
          {u.must_change_password && <span className="pill pill-warning">Force change on next login</span>}
        </div>
        {tempPw && (
          <div className="mt-3 p-3 rounded-lg bg-[var(--cc-surface)]">
            <div className="label-eyebrow mb-1">Temporary password (one-time)</div>
            <div className="font-dash text-lg font-bold" data-testid="reset-temp-password">{tempPw}</div>
            <div className="text-xs text-[var(--cc-text-soft)] mt-1">Valid 24h. A reset email is sent to the user automatically.</div>
          </div>
        )}
      </div>
      <div className="cc-divider pt-5">
        <h3 className="font-dash font-bold mb-2">Multi-factor authentication</h3>
        <div className="flex items-center gap-3">
          <span className={`pill ${u.mfa_enabled ? "pill-success" : "pill-warning"}`}>{u.mfa_enabled ? "Enabled" : "Pending setup"}</span>
          {u.mfa_required && <span className="pill pill-info">Required</span>}
        </div>
        <p className="text-xs text-[var(--cc-text-soft)] mt-2">MFA is mandatory for every management role. Recovery requires backup codes or Super Admin reset.</p>
      </div>
    </div>
  );
}

function OnboardingTab({ u, isSuper, reload }) {
  const tasks = u.onboarding_tasks || [];
  const done = tasks.filter((t) => t.completed).length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  const bypass = async () => {
    try {
      await api.post(`/admin/users/${u.id}/onboarding-bypass`);
      toast.success("Onboarding bypassed (logged in audit)");
      reload();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="cc-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-dash font-bold">Onboarding progress</h3>
        {u.onboarding_bypass ? <span className="pill pill-warning">Bypassed by Super Admin</span> :
          u.onboarding_completed ? <span className="pill pill-success">Completed</span> :
          <span className="pill pill-warning">{pct}% complete</span>}
      </div>
      <div className="flex items-center gap-4 mb-5">
        <div className="flex-1"><div className="cc-progress"><div className="cc-progress-fill" style={{ width: `${pct}%` }} /></div></div>
        <div className="text-sm font-semibold whitespace-nowrap">{done}/{tasks.length}</div>
      </div>
      <div className="space-y-1">
        {tasks.map((t) => (
          <div key={t.id} className="flex items-center gap-2 py-1.5">
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${t.completed ? "bg-[var(--cc-success)] text-white" : "bg-[var(--cc-border)]"}`}>{t.completed ? "✓" : ""}</span>
            <span className={`text-sm flex-1 ${t.completed ? "line-through text-[var(--cc-text-soft)]" : ""}`}>{t.label}</span>
            <span className="text-[10px] uppercase tracking-wide text-[var(--cc-text-soft)]">{t.category}</span>
          </div>
        ))}
      </div>
      {!u.onboarding_completed && isSuper && (
        <div className="cc-divider pt-4 mt-4 flex justify-end">
          <button className="btn-secondary" onClick={bypass} data-testid="onboarding-bypass-button"><AlertTriangle className="w-4 h-4" /> Force bypass (audited)</button>
        </div>
      )}
    </div>
  );
}

function AuditTab({ audit }) {
  return (
    <div className="cc-card p-6">
      <h3 className="font-dash font-bold mb-3">Recent audit events</h3>
      {audit.length === 0 && <p className="text-sm text-[var(--cc-text-soft)]">No events yet.</p>}
      <div className="space-y-2">
        {audit.map((e) => (
          <div key={e.id} className="flex items-start gap-3 py-2 border-b border-[var(--cc-border)] last:border-0">
            <div className="w-2 h-2 rounded-full mt-1.5" style={{ background: e.status === "failed" ? "var(--cc-accent)" : "var(--cc-primary)" }} />
            <div className="flex-1">
              <div className="text-sm font-medium">{e.event_type}</div>
              <div className="text-xs text-[var(--cc-text-soft)]">{fmtDateTime(e.timestamp)} · {e.performed_by_name || "—"} · {e.reason || ""}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LifecycleTab({ u, isSuper, reload }) {
  const [showDeact, setShowDeact] = useState(false);
  const [reason, setReason] = useState("Resignation");
  const [lastDay, setLastDay] = useState("");
  const [reassign, setReassign] = useState("");
  const [recipients, setRecipients] = useState([]);

  useEffect(() => {
    if (showDeact && u.role === "recruiter") {
      api.get("/admin/users", { params: { role: "recruiter", status_filter: "active" } })
        .then(({ data }) => setRecipients(data.filter((x) => x.id !== u.id)));
    }
  }, [showDeact]);

  const deactivate = async () => {
    try {
      await api.post(`/admin/users/${u.id}/deactivate`, {
        reason, last_working_day: lastDay || null,
        reassign_to_user_id: reassign || null,
      });
      toast.success("User deactivated");
      setShowDeact(false);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reactivate = async () => {
    try {
      await api.post(`/admin/users/${u.id}/reactivate`);
      toast.success("User reactivated");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="cc-card p-6">
      <h3 className="font-dash font-bold mb-3">Account lifecycle</h3>
      {u.status === "active" ? (
        <>
          {!showDeact ? (
            <button className="btn-danger" onClick={() => setShowDeact(true)} disabled={!isSuper} data-testid="show-deactivate-button"><Power className="w-4 h-4" /> Deactivate account</button>
          ) : (
            <div className="space-y-3 max-w-lg">
              <div><label className="label-eyebrow block mb-1">Reason</label>
                <select className="cc-select" value={reason} onChange={(e) => setReason(e.target.value)}>
                  <option>Resignation</option><option>Termination</option><option>Contract End</option><option>Retirement</option><option>Other</option>
                </select></div>
              <div><label className="label-eyebrow block mb-1">Last working day</label>
                <input type="date" className="cc-input" value={lastDay} onChange={(e) => setLastDay(e.target.value)} /></div>
              {u.role === "recruiter" && (u.assigned_job_ids?.length > 0) && (
                <div>
                  <label className="label-eyebrow block mb-1">Reassign {u.assigned_job_ids.length} jobs to (MANDATORY)</label>
                  <select className="cc-select" value={reassign} onChange={(e) => setReassign(e.target.value)} data-testid="reassign-to-select">
                    <option value="">Select another recruiter…</option>
                    {recipients.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button className="btn-ghost" onClick={() => setShowDeact(false)}>Cancel</button>
                <button className="btn-danger" onClick={deactivate} data-testid="confirm-deactivate-button">Confirm deactivation</button>
              </div>
            </div>
          )}
          {!isSuper && <p className="text-xs text-[var(--cc-text-soft)] mt-2">Only Super Admin can deactivate accounts.</p>}
        </>
      ) : (
        <>
          <div className="mb-3 text-sm text-[var(--cc-text-soft)]">
            Deactivated {fmtDateTime(u.deactivated_at)} · Reason: {u.deactivation_reason}
          </div>
          <button className="btn-primary" onClick={reactivate} disabled={!isSuper} data-testid="reactivate-button"><RefreshCw className="w-4 h-4" /> Reactivate</button>
        </>
      )}
    </div>
  );
}

// =============== EMAIL LOG PAGE ===============
export function AdminEmailLogPage() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/email-log").then(({ data }) => setRows(data)); }, []);
  if (me?.role !== "super_admin" && me?.role !== "hr_admin") return <div className="cc-card p-8 text-[var(--cc-text-soft)]">Insufficient permissions.</div>;
  return (
    <div className="fade-up">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">Communications</div>
        <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Mail className="w-8 h-8" />Email Log</h1>
        <p className="text-[var(--cc-text-soft)] mt-1">Every transactional email triggered by the system — provider, status, message ID and tag.</p>
      </div>
      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">When</th>
              <th className="px-5 py-3 label-eyebrow">To</th>
              <th className="px-5 py-3 label-eyebrow">Subject</th>
              <th className="px-5 py-3 label-eyebrow">Tag</th>
              <th className="px-5 py-3 label-eyebrow">Provider</th>
              <th className="px-5 py-3 label-eyebrow">Status</th>
              <th className="px-5 py-3 label-eyebrow">Message ID</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--cc-border)]" data-testid={`email-log-${r.id}`}>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)] whitespace-nowrap">{fmtDateTime(r.sent_at)}</td>
                <td className="px-5 py-3">{r.to_name}<div className="text-xs text-[var(--cc-text-soft)]">{r.to_email}</div></td>
                <td className="px-5 py-3 max-w-xs truncate">{r.subject}</td>
                <td className="px-5 py-3"><span className="pill pill-info">{r.tag}</span></td>
                <td className="px-5 py-3 text-xs">{r.provider === "brevo" ? <span className="pill pill-sage">brevo</span> : <span className="pill pill-warning">mocked</span>}</td>
                <td className="px-5 py-3">
                  <span className={`pill ${r.status === "sent" ? "pill-success" : r.status === "failed" ? "pill-danger" : r.status === "mocked" ? "pill-warning" : "pill-neutral"}`}>{r.status}</span>
                  {r.error && <div className="text-[10px] text-[var(--cc-accent)] mt-1 max-w-xs truncate" title={r.error}>{r.error}</div>}
                </td>
                <td className="px-5 py-3 text-xs font-mono text-[var(--cc-text-soft)]">{r.message_id?.slice(0, 12) || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-[var(--cc-text-soft)]">No emails sent yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminAuditPage() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/admin/audit", { params: { limit: 500 } }).then(({ data }) => setRows(data));
  }, []);

  if (me?.role !== "super_admin" && me?.role !== "hr_admin") {
    return <div className="cc-card p-8 text-[var(--cc-text-soft)]">Insufficient permissions.</div>;
  }

  const filtered = rows.filter((r) => !filter || r.event_type?.includes(filter));

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/audit/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `careconnect-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">Compliance</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><History className="w-8 h-8" />Audit Trail</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Immutable record of every user-management action. 7-year retention. CQC-ready.</p>
        </div>
        <button className="btn-secondary" onClick={exportCsv} data-testid="audit-export-button"><Download className="w-4 h-4" /> Export CSV</button>
      </div>

      <div className="cc-card p-4 mb-4">
        <div className="relative max-w-sm">
          <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--cc-text-soft)]" />
          <input className="cc-input pl-9" placeholder="Filter by event type… (e.g. user.created)" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="audit-filter-input" />
        </div>
      </div>

      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">When</th>
              <th className="px-5 py-3 label-eyebrow">Event</th>
              <th className="px-5 py-3 label-eyebrow">Performed by</th>
              <th className="px-5 py-3 label-eyebrow">Target</th>
              <th className="px-5 py-3 label-eyebrow">Reason</th>
              <th className="px-5 py-3 label-eyebrow">IP</th>
              <th className="px-5 py-3 label-eyebrow">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-[var(--cc-border)] hover:bg-[var(--cc-surface)]" data-testid={`audit-row-${r.id}`}>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)] whitespace-nowrap">{fmtDateTime(r.timestamp)}</td>
                <td className="px-5 py-3 font-mono text-xs">{r.event_type}</td>
                <td className="px-5 py-3">{r.performed_by_name || "—"}</td>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)]">{r.target_user_id ? r.target_user_id.slice(0, 8) + "…" : "—"}</td>
                <td className="px-5 py-3 text-xs">{r.reason || "—"}</td>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)]">{r.ip_address || "—"}</td>
                <td className="px-5 py-3"><span className={`pill ${r.status === "failed" ? "pill-danger" : "pill-success"}`}>{r.status}</span></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-[var(--cc-text-soft)]">No events.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
